import React, { useEffect, useState } from "react";
import { BaggageDetail, BaggageDetails, Booking } from "../../types/types";
import {
  completeBooking,
  deleteBooking,
  getAllBookings,
  updateBookingPnr,
  updateBookingTicketNumber,
  updatePassengerTicketNumber, // ✅ Imported new API
  updateStatus,
  updateBookingCurrency,
  updateFlightDetails,
  updatePassengerDetails,
  updateBookingRecord,
  updateBaggageDetails,
} from "./bookingController";
import { formatDate } from "../../utils/formatDate";
import {
  ChevronDown,
  ChevronUp,
  Search,
  Edit2,
  Edit3,
  Check,
  X,
  Loader2,
  RefreshCw,
} from "lucide-react";
import DeleteButton from "../../components/common/buttons/DeleteButton";
import PrimaryButton from "../../components/common/buttons/PrimaryButton";
import { useNavigate } from "react-router-dom";
import LoadingIndicator from "../../components/common/loadingIndicator/LoadingIndicator";
import Countdown from "react-countdown";
import CompleteBookingModal, {
  CompletionDetails,
} from "./CompleteBookingModal";

import { updateBookingTimer } from "./bookingController";

const parseSafeTime = (val: any) => {
  if (!val) return NaN;
  let str = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(str)) {
    str = str.replace(/\s+/, "T");
  }
  return new Date(str).getTime();
};

const Card: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className = "",
}) => (
  <div className={`bg-white shadow-md rounded-lg overflow-hidden ${className}`}>
    {children}
  </div>
);

const CardHeader: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className = "",
}) => (
  <div className={`px-6 py-4 bg-gray-50 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardContent: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className = "" }) => (
  <div className={`px-6 py-4 ${className}`}>{children}</div>
);

const ActionDropdown: React.FC<{
  booking: Booking;
  onStatusChange: (controlNumber: string, status: string) => Promise<void>;
  navigate: (path: string, state: any) => void;
  onActionSelect: (status: string) => void;
  selectedStatus: string;
}> = ({
  booking,
  onStatusChange,
  navigate,
  onActionSelect,
  selectedStatus,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [
    {
      label: "Pending",
      displayLabel: "Pending",
      disabled: booking.bookingDetails.status === "Pending",
      status: "Pending",
    },
    {
      label: "Mark as Complete",
      displayLabel: "Completed",
      disabled: booking.bookingDetails.status === "Completed",
      status: "Completed",
    },
    {
      label: "Mark as Cancel",
      displayLabel: "Cancelled",
      disabled: booking.bookingDetails.status === "Cancelled",
      status: "Cancelled",
    },
    {
      label: "Mark as Refund",
      displayLabel: "Refunded",
      disabled: booking.bookingDetails.status === "Refund",
      status: "Refunded",
    },
    {
      label: "Mark as Void",
      displayLabel: "Voided",
      disabled: booking.bookingDetails.status === "Void",
      status: "Voided",
    },
  ];

  const selectedAction = actions.find(
    (action) => action.status === selectedStatus,
  );
  const displayText = selectedAction
    ? selectedAction.displayLabel
    : "Select Action";

  useEffect(() => {
    // Set initial selection to Void
    onActionSelect("Pending");
  }, [onActionSelect]);

  return (
    <div className="relative inline-block text-left">
      <PrimaryButton
        onClick={() => setIsOpen(!isOpen)}
        className="min-w-[300px] px-4 py-2 bg-white text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 hover:text-blue-600 hover:border-blue-600 transition-all duration-200 flex items-center justify-center gap-2 font-medium"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Select an action"
      >
        <span style={{ color: selectedStatus ? "blue" : "black" }}>
          {displayText}
        </span>
        <ChevronDown
          size={16}
          className={`text-black transform transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </PrimaryButton>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-5"
            style={{ zIndex: 9998 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            className="absolute right-0 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5"
            style={{
              zIndex: 9999,
              maxHeight: "300px",
              minWidth: "200px",
              bottom: "100%",
              marginBottom: "0.5rem",
            }}
          >
            <div className="py-1 overflow-auto max-h-64">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => {
                    onActionSelect(action.status);
                    setIsOpen(false);
                  }}
                  disabled={action.disabled}
                  className={`w-full text-left px-4 py-2 text-sm ${
                    action.disabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-100"
                  } ${index === 0 ? "rounded-t-md" : ""} ${
                    index === actions.length - 1 ? "rounded-b-md" : ""
                  }`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Bookings: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(true);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [filterStatus, setFilterStatus] = useState<string>("Pending");
  const [expandedBookings, setExpandedBookings] = useState<Set<string>>(
    new Set(),
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const [editingPnrId, setEditingPnrId] = useState<number | null>(null);
  const [tempPnrValue, setTempPnrValue] = useState<string>("");
  const [isSavingPnr, setIsSavingPnr] = useState(false);

  const [editingTicketId, setEditingTicketId] = useState<number | null>(null);
  const [tempTicketValue, setTempTicketValue] = useState<string>("");
  const [isSavingTicket, setIsSavingTicket] = useState(false);

  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [selectedBookingForCompletion, setSelectedBookingForCompletion] =
    useState<Booking | null>(null);

  const [isTimerModalOpen, setIsTimerModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null,
  );
  const [newTimerDate, setNewTimerDate] = useState<string>("");
  const [isUpdatingTimer, setIsUpdatingTimer] = useState(false);

  useEffect(() => {
    const fetchBookingData = async () => {
      const bookingData = await getAllBookings();
      const sortedData = bookingData.sort(
        (a, b) =>
          new Date(b.bookingDetails.createdAt).getTime() -
          new Date(a.bookingDetails.createdAt).getTime(),
      );
      setBookings(sortedData);
      setFilteredBookings(sortedData);
      setIsProcessing(false);
    };
    fetchBookingData();
  }, []);

  useEffect(() => {
    const term = searchTerm.toLowerCase();

    const results = bookings.filter((booking) => {
      const matchesIds =
        booking.controlNumber.toLowerCase().includes(term) ||
        booking.pairId.toLowerCase().includes(term);

      const matchesPassenger = booking.bookingDetails.passengers?.some((p) => {
        const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
        return fullName.includes(term);
      });

      return matchesIds || matchesPassenger;
    });

    setFilteredBookings(results);
  }, [searchTerm, bookings]);

  const fmtUnit = (u?: string) => {
    if (!u) return "";
    const v = u.toLowerCase();
    if (v.includes("pc") || v.includes("piece")) return "pcs";
    if (v === "kg" || v === "kgs") return "Kg";
    return u;
  };

  const hasAnyBaggage = (bd?: any) => {
    if (!bd) return false;
    return !!(
      bd.outbound?.adult ||
      bd.outbound?.child ||
      bd.outbound?.infant ||
      bd.return?.adult ||
      bd.return?.child ||
      bd.return?.infant
    );
  };

  const handleTimerUpdate = async () => {
    if (!selectedBookingId || !newTimerDate) return;

    setIsUpdatingTimer(true); // Start Loader

    // Add time timezone offset locally if needed, usually datetime-local is fine
    const ISOString = new Date(newTimerDate).toISOString();

    const success = await updateBookingTimer(selectedBookingId, ISOString);

    if (success) {
      // Refresh your bookings list to show the new timer
      const fetchedBookings = await getAllBookings();

      // Sort them to match the initial load behavior
      const sortedData = fetchedBookings.sort(
        (a, b) =>
          new Date(b.bookingDetails.createdAt).getTime() -
          new Date(a.bookingDetails.createdAt).getTime(),
      );

      setBookings(sortedData);

      // Re-apply whatever filter is currently active so the UI doesn't break
      if (filterStatus === "Pending") {
        setFilteredBookings(
          sortedData.filter((b) => b.bookingDetails.paymentInfo.status === 0),
        );
      } else if (filterStatus === "Paid") {
        setFilteredBookings(
          sortedData.filter((b) => b.bookingDetails.paymentInfo.status === 1),
        );
      } else {
        setFilteredBookings(sortedData);
      }
    } else {
      alert("Failed to update timer. Please try again.");
    }

    setIsUpdatingTimer(false); // Stop Loader
    setIsTimerModalOpen(false); // Close Modal
  };

  const handleSort = (order: "asc" | "desc") => {
    setSortOrder(order);
    const sorted = [...filteredBookings].sort((a, b) => {
      const dateA = new Date(a.bookingDetails.createdAt).getTime();
      const dateB = new Date(b.bookingDetails.createdAt).getTime();
      return order === "asc" ? dateA - dateB : dateB - dateA;
    });
    setFilteredBookings(sorted);
  };

  const handleFilter = (status: string) => {
    setFilterStatus(status);
    if (status === "Pending") {
      const pendingBookings = bookings.filter(
        (booking) => booking.bookingDetails.paymentInfo.status === 0,
      );
      setFilteredBookings(pendingBookings);
    } else if (status === "Paid") {
      const paidBookings = bookings.filter(
        (booking) => booking.bookingDetails.paymentInfo.status === 1,
      );
      setFilteredBookings(paidBookings);
    } else {
      setFilteredBookings(bookings);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedBookings((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteBooking(id);
      setBookings((prev) => prev.filter((booking) => booking.id !== id));
      setFilteredBookings((prev) =>
        prev.filter((booking) => booking.id !== id),
      );
    } catch (error) {}
  };

  const startEditingPnr = (id: number, currentPnr: string) => {
    setEditingPnrId(id);
    setTempPnrValue(currentPnr);
  };

  const cancelEditingPnr = () => {
    setEditingPnrId(null);
    setTempPnrValue("");
  };

  const savePnr = async (id: number) => {
    if (!tempPnrValue.trim()) return;
    setIsSavingPnr(true);

    const success = await updateBookingPnr(id, tempPnrValue);

    if (success) {
      const updateList = (list: Booking[]) =>
        list.map((b) =>
          b.id === id ? { ...b, controlNumber: tempPnrValue } : b,
        );

      setBookings((prev) => updateList(prev));
      setFilteredBookings((prev) => updateList(prev));
      setEditingPnrId(null);
    } else {
      alert("Failed to update PNR");
    }
    setIsSavingPnr(false);
  };

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const currentBookings = filteredBookings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const startEditingTicket = (id: number, currentTicket: string) => {
    setEditingTicketId(id);
    setTempTicketValue(currentTicket || "");
  };

  const cancelEditingTicket = () => {
    setEditingTicketId(null);
    setTempTicketValue("");
  };

  const saveTicket = async (id: number) => {
    if (!tempTicketValue.trim()) return;
    setIsSavingTicket(true);

    const success = await updateBookingTicketNumber(id, tempTicketValue);

    if (success) {
      const updateList = (list: Booking[]) =>
        list.map((b) =>
          b.id === id
            ? {
                ...b,
                ticketNumber: tempTicketValue,
                bookingDetails: {
                  ...b.bookingDetails,
                  ticketNumber: tempTicketValue,
                },
              }
            : b,
        );

      setBookings((prev) => updateList(prev));
      setFilteredBookings((prev) => updateList(prev));
      setEditingTicketId(null);
    } else {
      alert("Failed to update Ticket Number");
    }
    setIsSavingTicket(false);
  };

  const BookingDetails: React.FC<{ booking: Booking }> = ({ booking }) => {
    const [bookingStatus, setBookingStatus] = useState<Booking>(booking);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedStatus, setSelectedStatus] = useState<string>("Pending");
    const [isUpdatingCurrency, setIsUpdatingCurrency] = useState(false);

    const [editingPaxId, setEditingPaxId] = useState<number | null>(null);
    const [tempPaxTicket, setTempPaxTicket] = useState<string>("");
    const [isSavingPaxTicket, setIsSavingPaxTicket] = useState(false);

    const [editingPaxIdFull, setEditingPaxIdFull] = useState<number | null>(
      null,
    );
    const [paxEditForm, setPaxEditForm] = useState<any>({});
    const [isSavingPaxDetails, setIsSavingPaxDetails] = useState(false);

    const [editingFlightId, setEditingFlightId] = useState<string | null>(null);
    const [flightEditForm, setFlightEditForm] = useState<any>({});
    const [isSavingFlightDetails, setIsSavingFlightDetails] = useState(false);

    const [editingBaggageFull, setEditingBaggageFull] = useState<number | null>(
      null,
    );
    const [baggageDetailsForm, setBaggageDetailsForm] = useState<any>({});
    const [isSavingBaggage, setIsSavingBaggage] = useState(false);

    useEffect(() => {
      setBookingStatus(booking);
      setSelectedStatus("");
    }, [booking]);

    const parseAmadeusDate = (dateStr: string) => {
      if (!dateStr) return "";
      // If it's already YYYY-MM-DD, just return it
      if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);

      const match = dateStr.match(/^(\d{2})([A-Z]{3})(\d{2,4})$/i);
      if (match) {
        const day = match[1];
        const monthStr = match[2].toLowerCase();
        let year = parseInt(match[3], 10);
        const months: Record<string, string> = {
          jan: "01",
          feb: "02",
          mar: "03",
          apr: "04",
          may: "05",
          jun: "06",
          jul: "07",
          aug: "08",
          sep: "09",
          oct: "10",
          nov: "11",
          dec: "12",
        };
        const month = months[monthStr] || "01";

        if (year < 100) {
          const currentYear = new Date().getFullYear() % 100;
          year += year > currentYear + 10 ? 1900 : 2000;
        }
        return `${year}-${month}-${day}`;
      }

      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
      return dateStr;
    };

    const taxAmount: number = Number(
      bookingStatus.bookingDetails.paymentInfo.taxAmount,
    );

    const totalAmount: number = Number(
      bookingStatus.bookingDetails.paymentInfo.totalAmount,
    );

    // ✅ HANDLER TO SAVE INDIVIDUAL PASSENGER TICKET
    const handleSavePaxTicket = async (passengerId: number) => {
      if (!tempPaxTicket.trim()) return;
      setIsSavingPaxTicket(true);

      const success = await updatePassengerTicketNumber(
        passengerId,
        tempPaxTicket,
      );

      if (success) {
        // 1. Update Local UI State
        setBookingStatus((prev) => ({
          ...prev,
          bookingDetails: {
            ...prev.bookingDetails,
            passengers: prev.bookingDetails.passengers.map((p) =>
              p.passengerId === passengerId
                ? { ...p, ticketNumber: tempPaxTicket }
                : p,
            ),
          },
        }));

        // 2. If it's the First Passenger, sync it to the Main PNR as well (Optional but useful)
        const isLeadPax =
          bookingStatus.bookingDetails.passengers[0]?.passengerId ===
          passengerId;
        if (isLeadPax) {
          await updateBookingTicketNumber(bookingStatus.id, tempPaxTicket);
        }

        // 3. Update the global lists so it doesn't revert on collapse
        setBookings((prevList) =>
          prevList.map((b) =>
            b.controlNumber === bookingStatus.controlNumber
              ? {
                  ...b,
                  ticketNumber: isLeadPax ? tempPaxTicket : b.ticketNumber,
                  bookingDetails: {
                    ...b.bookingDetails,
                    passengers: b.bookingDetails.passengers.map((p) =>
                      p.passengerId === passengerId
                        ? { ...p, ticketNumber: tempPaxTicket }
                        : p,
                    ),
                  },
                }
              : b,
          ),
        );

        setFilteredBookings((prevList) =>
          prevList.map((b) =>
            b.controlNumber === bookingStatus.controlNumber
              ? {
                  ...b,
                  ticketNumber: isLeadPax ? tempPaxTicket : b.ticketNumber,
                  bookingDetails: {
                    ...b.bookingDetails,
                    passengers: b.bookingDetails.passengers.map((p) =>
                      p.passengerId === passengerId
                        ? { ...p, ticketNumber: tempPaxTicket }
                        : p,
                    ),
                  },
                }
              : b,
          ),
        );

        setEditingPaxId(null);
      } else {
        alert("Failed to update passenger ticket.");
      }
      setIsSavingPaxTicket(false);
    };

    const handleSaveDetailedBaggage = async (bookingId: number) => {
      setIsSavingBaggage(true);
      const baggageData: any[] = [];

      // Transform UI state to DB format
      ["outbound", "return"].forEach((jType) => {
        ["adult", "child", "infant"].forEach((pType) => {
          const item = baggageDetailsForm[jType]?.[pType];
          if (item && item.allowance) {
            baggageData.push({
              journeyType: jType,
              passengerType:
                pType === "adult" ? "ADT" : pType === "child" ? "CHD" : "INF",
              freeAllowance: item.allowance.toString(),
              unitQualifier:
                item.unit === "Kg" || item.unit === "K" ? "K" : "N",
            });
          }
        });
      });

      const success = await updateBaggageDetails(bookingId, baggageData);
      if (success) {
        setBookingStatus((prev: any) => ({
          ...prev,
          baggageDetails: baggageDetailsForm,
        }));
        setEditingBaggageFull(null);
      } else {
        alert("Failed to update baggage details.");
      }
      setIsSavingBaggage(false);
    };

    const handleSavePaxDetails = async (passengerId: number) => {
      setIsSavingPaxDetails(true);
      const success = await updatePassengerDetails(passengerId, paxEditForm);
      if (success) {
        setBookingStatus((prev) => ({
          ...prev,
          bookingDetails: {
            ...prev.bookingDetails,
            passengers: prev.bookingDetails.passengers.map((p) =>
              p.passengerId === passengerId ? { ...p, ...paxEditForm } : p,
            ),
          },
        }));
        setEditingPaxIdFull(null);
      } else {
        alert("Failed to update passenger details.");
      }
      setIsSavingPaxDetails(false);
    };

    const handleSaveFlightDetails = async (flightId: string | number) => {
      setIsSavingFlightDetails(true);

      // Convert to string for the API call just in case
      const success = await updateFlightDetails(
        String(flightId),
        flightEditForm,
      );

      if (success) {
        setBookingStatus((prev: any) => ({
          ...prev,
          bookingDetails: {
            ...prev.bookingDetails,
            flights: prev.bookingDetails.flights.map((f: any) =>
              // ✅ FIX: Convert both to Strings to safely compare without TS errors
              String(f.flightId) === String(flightId)
                ? { ...f, ...flightEditForm }
                : f,
            ),
          },
        }));
        setEditingFlightId(null);
      } else {
        alert("Failed to update flight details.");
      }
      setIsSavingFlightDetails(false);
    };

    const handleMarkAsCompleted = async () => {
      if (!selectedStatus) {
        alert("Please select an action first");
        return;
      }

      if (selectedStatus === "Completed") {
        setSelectedBookingForCompletion(bookingStatus);
        setIsCompleteModalOpen(true);
        return;
      }

      setIsLoading(true);
      try {
        const response = await updateStatus(
          bookingStatus.controlNumber,
          selectedStatus,
        );
        if (response === true) {
          let newPaymentStatus =
            bookingStatus.bookingDetails.paymentInfo.status;
          let newPaidAt: any = bookingStatus.bookingDetails.paymentInfo.paidAt;

          if (selectedStatus === "Completed") {
            newPaymentStatus = 1;
            newPaidAt = Date.now();
          } else if (
            ["Cancelled", "Refunded", "Voided"].includes(selectedStatus)
          ) {
            newPaymentStatus = 0;
            newPaidAt = null;
          }

          setBookingStatus((prev) => ({
            ...prev,
            bookingDetails: {
              ...prev.bookingDetails,
              status: selectedStatus,
              paymentInfo: {
                ...prev.bookingDetails.paymentInfo,
                status: newPaymentStatus,
                paidAt: newPaidAt,
              },
            },
          }));

          setBookings((prevBookings) =>
            prevBookings.map((booking) =>
              booking.controlNumber === bookingStatus.controlNumber
                ? {
                    ...booking,
                    bookingDetails: {
                      ...booking.bookingDetails,
                      status: selectedStatus,
                      paymentInfo: {
                        ...booking.bookingDetails.paymentInfo,
                        status: newPaymentStatus,
                        paidAt: newPaidAt,
                      },
                    },
                  }
                : booking,
            ),
          );

          setFilteredBookings((prevFilteredBookings) =>
            prevFilteredBookings.map((booking) =>
              booking.controlNumber === bookingStatus.controlNumber
                ? {
                    ...booking,
                    bookingDetails: {
                      ...booking.bookingDetails,
                      status: selectedStatus,
                      paymentInfo: {
                        ...booking.bookingDetails.paymentInfo,
                        status: newPaymentStatus,
                        paidAt: newPaidAt,
                      },
                    },
                  }
                : booking,
            ),
          );
        }
      } catch (error) {
        console.error("Error updating status:", error);
        alert("Failed to update status");
      } finally {
        setIsLoading(false);
      }
    };

    const handleCompleteBookingConfirm = async (details: CompletionDetails) => {
      if (!selectedBookingForCompletion) return;

      try {
        const success = await completeBooking(
          selectedBookingForCompletion.controlNumber,
          details,
        );

        if (success) {
          const updateList = (list: Booking[]) =>
            list.map((b) =>
              b.controlNumber === selectedBookingForCompletion.controlNumber
                ? ({
                    ...b,
                    bookingDetails: {
                      ...b.bookingDetails,
                      status: "Completed",
                      platform: details.platform,
                      issuedFrom: details.issuedFrom,
                      agentCategory: details.agentCategory,
                      sellingPrice: details.ticketSellingPrice,
                      paymentInfo: {
                        ...b.bookingDetails.paymentInfo,
                        status: 1,
                        paidAt: Date.now(),
                      },
                    },
                  } as Booking)
                : b,
            );

          setBookings((prev) => updateList(prev));
          setFilteredBookings((prev) => updateList(prev));

          setIsCompleteModalOpen(false);
          setSelectedBookingForCompletion(null);
        } else {
          alert("Failed to complete booking and sync data.");
        }
      } catch (error) {
        console.error("Error completing booking:", error);
        alert("An error occurred.");
      }
    };

    const totalPrice: number = taxAmount + totalAmount;

    const handleCurrencyToggle = async () => {
      if (isUpdatingCurrency) return;

      const currentCurrency = bookingStatus.bookingDetails.paymentInfo.currency;
      const newCurrency = currentCurrency === "EUR" ? "LKR" : "EUR";

      setIsUpdatingCurrency(true);

      const oldBookingState = { ...bookingStatus };

      setBookingStatus((prev) => ({
        ...prev,
        bookingDetails: {
          ...prev.bookingDetails,
          paymentInfo: {
            ...prev.bookingDetails.paymentInfo,
            currency: newCurrency,
          },
        },
      }));

      try {
        const success = await updateBookingCurrency(
          bookingStatus.controlNumber,
          newCurrency,
        );

        if (!success) {
          throw new Error("API failed");
        }

        setBookings((prev) =>
          prev.map((b) =>
            b.controlNumber === bookingStatus.controlNumber
              ? {
                  ...b,
                  bookingDetails: {
                    ...b.bookingDetails,
                    paymentInfo: {
                      ...b.bookingDetails.paymentInfo,
                      currency: newCurrency,
                    },
                  },
                }
              : b,
          ),
        );
      } catch (e) {
        setBookingStatus(oldBookingState);
        console.error("Currency update failed", e);
        alert("Failed to update currency.");
      } finally {
        setIsUpdatingCurrency(false);
      }
    };

    const presentPaxTypes = Array.from(
      new Set(
        bookingStatus.bookingDetails.passengers
          ?.map((p: any) => {
            const t = (p.type || "").toUpperCase();
            if (t === "ADT" || t === "ADULT") return "adult";
            if (t === "CHD" || t === "CHILD") return "child";
            if (t === "INF" || t === "INFANT") return "infant";
            return null;
          })
          .filter(Boolean),
      ),
    ) as string[];

    if (presentPaxTypes.length === 0) presentPaxTypes.push("adult");

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4 ">
        <Card>
          <CardHeader>
            <h3 className="text-xl font-semibold text-indigo-600">
              Passenger Details
            </h3>
          </CardHeader>
          <CardContent>
            {/* ✅ FIX: Added ': any' to passenger to bypass strict TS checking */}
            {bookingStatus.bookingDetails.passengers.map((passenger: any) => (
              <div
                key={passenger.passengerId}
                className="mb-4 last:mb-0 p-4 bg-white border border-gray-100 rounded-lg shadow-sm relative group"
              >
                {/* ✅ GLOBAL PASSENGER EDIT TOGGLE */}
                <button
                  onClick={() => {
                    setEditingPaxIdFull(passenger.passengerId);
                    setPaxEditForm({
                      title: passenger.title,
                      firstName: passenger.firstName,
                      lastName: passenger.lastName,
                      gender: passenger.gender,
                      type: passenger.type,
                      // ✅ Apply the parser to dates to populate the HTML inputs correctly
                      dateOfBirth: parseAmadeusDate(passenger.dateOfBirth),
                      passportNumber: passenger.passportNumber,
                      passportIssueDate: parseAmadeusDate(
                        passenger.passportIssueDate,
                      ),
                      passportExpiry: parseAmadeusDate(
                        passenger.passportExpiry,
                      ),
                    });
                  }}
                  className={`absolute top-4 right-4 p-1 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-indigo-600 transition-opacity ${
                    editingPaxIdFull === passenger.passengerId ? "hidden" : ""
                  }`}
                  title="Edit Passenger Details"
                >
                  <Edit2 size={16} />
                </button>

                {editingPaxIdFull === passenger.passengerId ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-2">
                    <div>
                      <label className="text-xs text-gray-500">Title</label>
                      <input
                        type="text"
                        className="border w-full p-1 rounded"
                        value={paxEditForm.title || ""}
                        onChange={(e) =>
                          setPaxEditForm({
                            ...paxEditForm,
                            title: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">
                        First Name
                      </label>
                      <input
                        type="text"
                        className="border w-full p-1 rounded"
                        value={paxEditForm.firstName || ""}
                        onChange={(e) =>
                          setPaxEditForm({
                            ...paxEditForm,
                            firstName: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Last Name</label>
                      <input
                        type="text"
                        className="border w-full p-1 rounded"
                        value={paxEditForm.lastName || ""}
                        onChange={(e) =>
                          setPaxEditForm({
                            ...paxEditForm,
                            lastName: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Gender</label>
                      <input
                        type="text"
                        className="border w-full p-1 rounded"
                        value={paxEditForm.gender || ""}
                        onChange={(e) =>
                          setPaxEditForm({
                            ...paxEditForm,
                            gender: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Type</label>
                      <input
                        type="text"
                        className="border w-full p-1 rounded"
                        value={paxEditForm.type || ""}
                        onChange={(e) =>
                          setPaxEditForm({
                            ...paxEditForm,
                            type: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">DOB</label>
                      <input
                        type="date"
                        className="border w-full p-1 rounded"
                        value={paxEditForm.dateOfBirth || ""}
                        onChange={(e) =>
                          setPaxEditForm({
                            ...paxEditForm,
                            dateOfBirth: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">
                        Passport No
                      </label>
                      <input
                        type="text"
                        className="border w-full p-1 rounded"
                        value={paxEditForm.passportNumber || ""}
                        onChange={(e) =>
                          setPaxEditForm({
                            ...paxEditForm,
                            passportNumber: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">
                        Issue Date
                      </label>
                      <input
                        type="date"
                        className="border w-full p-1 rounded"
                        value={paxEditForm.passportIssueDate || ""}
                        onChange={(e) =>
                          setPaxEditForm({
                            ...paxEditForm,
                            passportIssueDate: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        className="border w-full p-1 rounded"
                        value={paxEditForm.passportExpiry || ""}
                        onChange={(e) =>
                          setPaxEditForm({
                            ...paxEditForm,
                            passportExpiry: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="md:col-span-2 flex gap-2 mt-2">
                      <PrimaryButton
                        onClick={() =>
                          handleSavePaxDetails(passenger.passengerId)
                        }
                        disabled={isSavingPaxDetails}
                        className="py-1 px-3 text-sm flex items-center justify-center"
                      >
                        Save Details
                      </PrimaryButton>
                      <button
                        onClick={() => setEditingPaxIdFull(null)}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p>
                      <strong>Name:</strong>{" "}
                      {passenger.title ? `${passenger.title} ` : ""}
                      {passenger.firstName} {passenger.lastName}
                    </p>

                    {/* ✅ INDIVIDUAL PASSENGER TICKET ROW */}
                    <div className="flex items-center gap-2 mt-2 mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Ticket No:
                      </span>
                      {editingPaxId === passenger.passengerId ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={tempPaxTicket}
                            onChange={(e) => setTempPaxTicket(e.target.value)}
                            className="border border-indigo-300 rounded px-2 py-1 text-sm font-semibold text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-40"
                            placeholder="Enter ticket"
                          />
                          <button
                            onClick={() =>
                              handleSavePaxTicket(passenger.passengerId)
                            }
                            disabled={isSavingPaxTicket}
                            className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                            title="Save"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => setEditingPaxId(null)}
                            className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group/ticket">
                          <span
                            className={`text-sm font-semibold ${
                              passenger.ticketNumber
                                ? "text-indigo-600"
                                : "text-orange-500"
                            }`}
                          >
                            {passenger.ticketNumber || "Pending"}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingPaxId(passenger.passengerId);
                              setTempPaxTicket(passenger.ticketNumber || "");
                            }}
                            className="p-1 text-gray-400 opacity-0 group-hover/ticket:opacity-100 hover:text-indigo-600 transition-opacity"
                            title="Edit Ticket No"
                          >
                            <Edit2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    <p>
                      <strong>Gender:</strong> {passenger.gender} |{" "}
                      <strong>Type:</strong> {passenger.type}
                    </p>
                    <p>
                      <strong>Date of Birth:</strong> {passenger.dateOfBirth}
                    </p>
                    <p>
                      <strong>Passport Number:</strong>{" "}
                      {passenger.passportNumber}
                    </p>
                    <p>
                      <strong>Passport Issue Date:</strong>{" "}
                      {passenger.passportIssueDate}
                    </p>
                    <p>
                      <strong>Passport Expiry Date:</strong>{" "}
                      {passenger.passportExpiry}
                    </p>
                  </>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-xl font-semibold text-indigo-600">
              Flight Details
            </h3>
          </CardHeader>
          <CardContent>
            {/* ✅ FIX: Added ': any' to flight to bypass strict TS checking */}
            {bookingStatus.bookingDetails.flights.map(
              (flight: any, index: number) => (
                <div
                  key={flight.flightId}
                  className={`relative group ${
                    index > 0 ? "pt-4 border-t border-gray-200" : ""
                  }`}
                >
                  {/* ✅ GLOBAL FLIGHT EDIT TOGGLE */}
                  <button
                    onClick={() => {
                      setEditingFlightId(flight.flightId);
                      setFlightEditForm({
                        flightNumber: flight.flightNumber,
                        airline: flight.airline,
                        origin: flight.origin,
                        destination: flight.destination,
                        departureDateTime: flight.departureDateTime
                          ? flight.departureDateTime.slice(0, 16)
                          : "",
                        arrivalDateTime: flight.arrivalDateTime
                          ? flight.arrivalDateTime.slice(0, 16)
                          : "",
                      });
                    }}
                    className={`absolute top-0 right-4 p-1 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-indigo-600 transition-opacity ${
                      editingFlightId === flight.flightId ? "hidden" : ""
                    }`}
                    title="Edit Flight Details"
                  >
                    <Edit2 size={16} />
                  </button>

                  {editingFlightId === flight.flightId ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-4">
                      <div>
                        <label className="text-xs text-gray-500">
                          Flight Number
                        </label>
                        <input
                          type="text"
                          className="border w-full p-1 rounded"
                          value={flightEditForm.flightNumber || ""}
                          onChange={(e) =>
                            setFlightEditForm({
                              ...flightEditForm,
                              flightNumber: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Airline</label>
                        <input
                          type="text"
                          className="border w-full p-1 rounded"
                          value={flightEditForm.airline || ""}
                          onChange={(e) =>
                            setFlightEditForm({
                              ...flightEditForm,
                              airline: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Origin Route
                        </label>
                        <input
                          type="text"
                          className="border w-full p-1 rounded"
                          value={flightEditForm.origin || ""}
                          onChange={(e) =>
                            setFlightEditForm({
                              ...flightEditForm,
                              origin: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Destination Route
                        </label>
                        <input
                          type="text"
                          className="border w-full p-1 rounded"
                          value={flightEditForm.destination || ""}
                          onChange={(e) =>
                            setFlightEditForm({
                              ...flightEditForm,
                              destination: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Departure Time
                        </label>
                        <input
                          type="datetime-local"
                          className="border w-full p-1 rounded"
                          value={flightEditForm.departureDateTime || ""}
                          onChange={(e) =>
                            setFlightEditForm({
                              ...flightEditForm,
                              departureDateTime: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Arrival Time
                        </label>
                        <input
                          type="datetime-local"
                          className="border w-full p-1 rounded"
                          value={flightEditForm.arrivalDateTime || ""}
                          onChange={(e) =>
                            setFlightEditForm({
                              ...flightEditForm,
                              arrivalDateTime: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="md:col-span-2 flex gap-2 mt-2">
                        <PrimaryButton
                          onClick={() =>
                            handleSaveFlightDetails(flight.flightId)
                          }
                          disabled={isSavingFlightDetails}
                          className="py-1 px-3 text-sm flex justify-center"
                        >
                          Save Flight
                        </PrimaryButton>
                        <button
                          onClick={() => setEditingFlightId(null)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <p>
                        <strong>Flight:</strong> {flight.flightNumber} |{" "}
                        <strong>Airline:</strong> {flight.airline}
                      </p>
                      <p>
                        <strong>Route:</strong> {flight.origin} to{" "}
                        {flight.destination}
                      </p>
                      <p>
                        <strong>Departure:</strong>{" "}
                        {new Date(flight.departureDateTime).toLocaleString()}
                      </p>
                      <p>
                        <strong>Arrival:</strong>{" "}
                        {new Date(flight.arrivalDateTime).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              ),
            )}

            {/* ✅ DETAILED BAGGAGE ALLOWANCE SECTION */}
            <div className="mt-4 pt-4 border-t border-gray-200 relative group">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold text-sm text-gray-700">
                  <svg
                    className="inline-block w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Baggage Allowance Details
                </h4>
                <button
                  onClick={() => {
                    setEditingBaggageFull(bookingStatus.bookingDetails.id);
                    // ✅ FIX: Robust Initialization. Safely load existing data so fields are NOT empty.
                    const initial = bookingStatus.baggageDetails
                      ? JSON.parse(JSON.stringify(bookingStatus.baggageDetails))
                      : {};
                    if (!initial.outbound) initial.outbound = {};
                    if (!initial.return) initial.return = {};
                    setBaggageDetailsForm(initial);
                  }}
                  className={`p-1 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-indigo-600 transition-opacity ${
                    editingBaggageFull === bookingStatus.bookingDetails.id
                      ? "hidden"
                      : ""
                  }`}
                  title="Edit Baggage Details"
                >
                  <Edit2 size={16} />
                </button>
              </div>

              {editingBaggageFull === bookingStatus.bookingDetails.id ? (
                <div className="mb-4">
                  {["outbound", "return"].map((journey) => (
                    <div
                      key={journey}
                      className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-100"
                    >
                      <h5 className="capitalize font-semibold text-gray-700 mb-3 border-b border-gray-200 pb-2">
                        {journey} Baggage
                      </h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* ✅ FIX: Only map over passenger types that ACTUALLY exist in the booking */}
                        {presentPaxTypes.map((type) => (
                          <div
                            key={type}
                            className="bg-white border border-gray-200 p-3 rounded shadow-sm"
                          >
                            <label className="text-sm text-gray-600 capitalize font-semibold">
                              {type}
                            </label>
                            {/* ✅ FIX: Increased input size & spacing */}
                            <div className="flex gap-2 mt-2">
                              <input
                                type="number"
                                className="border border-gray-300 p-2 w-1/2 text-sm rounded outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                                placeholder="Amount"
                                value={
                                  baggageDetailsForm[journey]?.[type]
                                    ?.allowance || ""
                                }
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setBaggageDetailsForm((prev: any) => ({
                                    ...prev,
                                    [journey]: {
                                      ...(prev[journey] || {}),
                                      [type]: {
                                        ...(prev[journey]?.[type] || {}),
                                        allowance: val,
                                      },
                                    },
                                  }));
                                }}
                              />
                              <select
                                className="border border-gray-300 p-2 w-1/2 text-sm rounded outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                                value={
                                  baggageDetailsForm[journey]?.[type]?.unit ===
                                    "Pieces" ||
                                  baggageDetailsForm[journey]?.[type]?.unit ===
                                    "N"
                                    ? "Pieces"
                                    : "Kg"
                                }
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setBaggageDetailsForm((prev: any) => ({
                                    ...prev,
                                    [journey]: {
                                      ...(prev[journey] || {}),
                                      [type]: {
                                        ...(prev[journey]?.[type] || {}),
                                        unit: val,
                                      },
                                    },
                                  }));
                                }}
                              >
                                <option value="Kg">Kg</option>
                                <option value="Pieces">Pieces</option>
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-3 mt-4">
                    <PrimaryButton
                      onClick={() =>
                        handleSaveDetailedBaggage(
                          bookingStatus.bookingDetails.id,
                        )
                      }
                      disabled={isSavingBaggage}
                      className="py-2 px-6 text-sm font-semibold flex items-center justify-center shadow-sm"
                    >
                      Save Baggage Details
                    </PrimaryButton>
                    <button
                      onClick={() => setEditingBaggageFull(null)}
                      className="px-6 py-2 bg-red-50 text-red-600 font-semibold rounded text-sm hover:bg-red-100 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Outbound Display */}
                  {bookingStatus.baggageDetails?.outbound && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-blue-700 mb-2">
                        → Outbound
                      </p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        {bookingStatus.baggageDetails.outbound.adult && (
                          <div className="bg-white rounded px-2 py-1">
                            <span className="text-gray-600">Adult:</span>
                            <span className="font-medium ml-1">
                              {
                                bookingStatus.baggageDetails.outbound.adult
                                  .allowance
                              }{" "}
                              {fmtUnit(
                                bookingStatus.baggageDetails.outbound.adult
                                  .unit,
                              )}
                            </span>
                          </div>
                        )}
                        {bookingStatus.baggageDetails.outbound.child && (
                          <div className="bg-white rounded px-2 py-1">
                            <span className="text-gray-600">Child:</span>
                            <span className="font-medium ml-1">
                              {
                                bookingStatus.baggageDetails.outbound.child
                                  .allowance
                              }{" "}
                              {fmtUnit(
                                bookingStatus.baggageDetails.outbound.child
                                  .unit,
                              )}
                            </span>
                          </div>
                        )}
                        {bookingStatus.baggageDetails.outbound.infant && (
                          <div className="bg-white rounded px-2 py-1">
                            <span className="text-gray-600">Infant:</span>
                            <span className="font-medium ml-1">
                              {
                                bookingStatus.baggageDetails.outbound.infant
                                  .allowance
                              }{" "}
                              {fmtUnit(
                                bookingStatus.baggageDetails.outbound.infant
                                  .unit,
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Return Display */}
                  {bookingStatus.baggageDetails?.return && (
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-green-700 mb-2">
                        ← Return
                      </p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        {bookingStatus.baggageDetails.return.adult && (
                          <div className="bg-white rounded px-2 py-1">
                            <span className="text-gray-600">Adult:</span>
                            <span className="font-medium ml-1">
                              {
                                bookingStatus.baggageDetails.return.adult
                                  .allowance
                              }{" "}
                              {fmtUnit(
                                bookingStatus.baggageDetails.return.adult.unit,
                              )}
                            </span>
                          </div>
                        )}
                        {bookingStatus.baggageDetails.return.child && (
                          <div className="bg-white rounded px-2 py-1">
                            <span className="text-gray-600">Child:</span>
                            <span className="font-medium ml-1">
                              {
                                bookingStatus.baggageDetails.return.child
                                  .allowance
                              }{" "}
                              {fmtUnit(
                                bookingStatus.baggageDetails.return.child.unit,
                              )}
                            </span>
                          </div>
                        )}
                        {bookingStatus.baggageDetails.return.infant && (
                          <div className="bg-white rounded px-2 py-1">
                            <span className="text-gray-600">Infant:</span>
                            <span className="font-medium ml-1">
                              {
                                bookingStatus.baggageDetails.return.infant
                                  .allowance
                              }{" "}
                              {fmtUnit(
                                bookingStatus.baggageDetails.return.infant.unit,
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!bookingStatus.baggageDetails?.outbound &&
                    !bookingStatus.baggageDetails?.return && (
                      <p className="text-sm text-gray-500 italic">
                        No detailed baggage allowance registered. Click edit to
                        add.
                      </p>
                    )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-xl font-semibold text-indigo-600">
              Payment & User Info
            </h3>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <strong>Total:</strong>

                <button
                  onClick={handleCurrencyToggle}
                  disabled={isUpdatingCurrency}
                  className="px-2 py-1 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded text-sm font-bold text-gray-700 transition-colors flex items-center gap-1 shadow-sm"
                  title="Click to toggle currency"
                >
                  {bookingStatus.bookingDetails.paymentInfo.currency}
                  {isUpdatingCurrency ? (
                    <Loader2 size={12} className="animate-spin text-blue-600" />
                  ) : (
                    <RefreshCw size={12} className="text-gray-400" />
                  )}
                </button>

                <span className="text-lg">{totalPrice.toFixed(2)}</span>
              </div>
              {bookingStatus.bookingDetails.status === "Completed" && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200 my-2 text-sm space-y-1">
                  <p>
                    <strong>Agent:</strong>{" "}
                    {bookingStatus.bookingDetails.agentCategory || "N/A"}
                  </p>
                  <p>
                    <strong>Platform:</strong>{" "}
                    {bookingStatus.bookingDetails.platform || "N/A"}
                  </p>
                  <p>
                    <strong>Issued From:</strong>{" "}
                    {bookingStatus.bookingDetails.issuedFrom || "N/A"}
                  </p>
                  <p>
                    <strong>Cost:</strong>{" "}
                    {bookingStatus.bookingDetails.paymentInfo.currency}{" "}
                    {Number(
                      bookingStatus.bookingDetails.sellingPrice || 0,
                    ).toFixed(2)}
                  </p>
                </div>
              )}
              <p>
                <strong>Status:</strong>{" "}
                <span
                  className={`w-min px-2 py-1 rounded-full ${
                    bookingStatus.bookingDetails.paymentInfo.status === 0
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {bookingStatus.bookingDetails.paymentInfo.status === 0
                    ? "Pending Payment"
                    : "Paid"}
                </span>
              </p>
              <p>
                <strong>Order No:</strong> {bookingStatus.controlNumber}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {formatDate(bookingStatus.bookingDetails.createdAt)}
              </p>
              <p>
                <strong>User Name:</strong> {bookingStatus.user.firstName}{" "}
                {bookingStatus.user.lastName}
              </p>
              <p>
                <strong>User Email:</strong> {bookingStatus.user.email}
              </p>
              <p>
                <strong>User Phone:</strong>{" "}
                {bookingStatus.user.contact || "N/A"}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between items-center lg:col-span-3">
          <PrimaryButton
            className="w-40"
            onClick={() =>
              navigate(`/bookings/ticket/${bookingStatus.controlNumber}`, {
                state: { booking: bookingStatus },
              })
            }
          >
            Create Ticket
          </PrimaryButton>

          <div className="flex items-center gap-4">
            <div className="relative">
              <ActionDropdown
                booking={bookingStatus}
                onStatusChange={async () => {}}
                navigate={navigate}
                onActionSelect={setSelectedStatus}
                selectedStatus={selectedStatus}
              />
              {selectedStatus && (
                <span className="absolute -top-2 -right-2 h-4 w-4 rounded-full"></span>
              )}
            </div>
            <PrimaryButton
              onClick={handleMarkAsCompleted}
              disabled={isLoading || !selectedStatus}
              className={`relative min-w-[180px] px-6 py-3 rounded-lg font-semibold transition-transform duration-300 shadow-md flex items-center justify-center gap-3 ${
                selectedStatus
                  ? "bg-blue-600 text-white hover:bg-blue-700 active:scale-95 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
              }`}
              aria-live={isLoading ? "polite" : "off"}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <LoadingIndicator /> <span>Updating...</span>
                </span>
              ) : (
                <>
                  <span className="inline-block">Update Status</span>
                </>
              )}
            </PrimaryButton>
          </div>
        </div>
        <CompleteBookingModal
          isOpen={isCompleteModalOpen}
          onClose={() => setIsCompleteModalOpen(false)}
          onConfirm={handleCompleteBookingConfirm}
          booking={bookingStatus}
        />
      </div>
    );
  };

  const timerRenderer = ({ days, hours, minutes, seconds, completed }: any) => {
    if (completed) {
      return <span className="text-red-600 font-bold">Timer is Completed</span>;
    }
    return (
      <span className="text-red-500">
        {String(days).padStart(2, "0")}:{String(hours).padStart(2, "0")}:
        {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
      </span>
    );
  };

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-3xl font-bold mb-9">Booking Management</h2>

      <div className="flex flex-wrap gap-4 mb-6">
        <div>
          <label htmlFor="sort" className="mr-2 font-semibold">
            Sort by Date:
          </label>
          <select
            id="sort"
            value={sortOrder}
            onChange={(e) => handleSort(e.target.value as "asc" | "desc")}
            className="border border-gray-300 rounded-md p-2"
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>

        <div>
          <label htmlFor="filter" className="mr-2 font-semibold">
            Filter by Status:
          </label>
          <select
            id="filter"
            value={filterStatus}
            onChange={(e) => handleFilter(e.target.value)}
            className="border border-gray-300 rounded-md p-2"
          >
            <option value="all">All</option>
            <option value="Pending">Pending</option>
            <option value="Paid">Paid</option>
          </select>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">
          {filterStatus} Bookings ({filteredBookings.length || 0})
        </h3>
        <div className="relative">
          <input
            type="text"
            placeholder="Search bookings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={20}
          />
        </div>
      </div>

      {isProcessing ? (
        <div className="flex flex-col items-center justify-center py-32 bg-white rounded-lg shadow-sm border border-gray-100">
          <LoadingIndicator />
          <h3 className="mt-6 text-lg font-semibold text-gray-800">
            Syncing Bookings...
          </h3>
          <p className="text-gray-500 mt-2">
            Fetching the latest booking details and ticket numbers from Amadeus.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {currentBookings.map((booking) => {
            const createdAtTime = parseSafeTime(
              booking.bookingDetails.createdAt,
            );
            const expiryTime = !isNaN(createdAtTime)
              ? createdAtTime + 6 * 60 * 60 * 1000
              : Date.now();
            const isExpired = Date.now() > expiryTime;

            // Calculate the absolute target time for both the clock and the edit modal
            const customTime = parseSafeTime(
              booking.bookingDetails.lastTicketDate,
            );
            const targetTime = !isNaN(customTime) ? customTime : expiryTime;

            const visualStatus =
              booking.bookingDetails.status === "Pending" && isExpired
                ? "Cancelled"
                : booking.bookingDetails.status;

            const leadPassenger = booking.bookingDetails.passengers?.[0];
            const clientName = leadPassenger
              ? `${leadPassenger.title ? leadPassenger.title + " " : ""}${leadPassenger.firstName} ${leadPassenger.lastName}`
              : "Unknown Passenger";

            // ✅ ADDED: Calculate all unique passenger tickets for display
            const passengerTickets =
              booking.bookingDetails?.passengers
                ?.map((p) => p.ticketNumber)
                .filter((t) => t && t.trim() !== "") || [];

            const uniqueTickets = Array.from(new Set(passengerTickets));
            const displayTickets =
              uniqueTickets.length > 0
                ? uniqueTickets.join(" / ")
                : booking.ticketNumber || "Pending";

            return (
              <Card key={booking.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">
                        Booking Ref:{" "}
                        <span className="text-blue-700">
                          {booking.bookingDetails.arBookingReference || "N/A"}
                        </span>
                        {booking.bookingDetails.flights?.[0]?.airline && (
                          <span className="text-gray-600">
                            {" "}
                            , (Airline : {booking.bookingDetails.flights[0].airline})
                          </span>
                        )}
                      </h2>
                      <div className="text-lg font-semibold text-gray-700 mt-1 flex items-center gap-2">
                        <span className="text-gray-500 text-sm uppercase tracking-wider font-bold">
                          Passenger:
                        </span>
                        {clientName}
                      </div>

                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500">
                          PNR / Control No:
                        </span>
                        {editingPnrId === booking.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={tempPnrValue}
                              onChange={(e) => setTempPnrValue(e.target.value)}
                              className="border border-indigo-300 rounded px-2 py-1 text-sm font-semibold text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-32"
                            />
                            <button
                              onClick={() => savePnr(booking.id)}
                              disabled={isSavingPnr}
                              className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                              title="Save"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={cancelEditingPnr}
                              className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group">
                            <span className="text-sm font-semibold text-indigo-600">
                              {booking.controlNumber}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingPnr(
                                  booking.id,
                                  booking.controlNumber,
                                );
                              }}
                              className="p-1 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-indigo-600 transition-opacity"
                              title="Edit PNR"
                            >
                              <Edit2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500">
                          Ticket No's:
                        </span>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-sm font-semibold ${
                              displayTickets !== "Pending"
                                ? "text-indigo-600"
                                : "text-orange-500"
                            }`}
                          >
                            {displayTickets}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500 flex items-center">
                        {booking.bookingDetails.status === "Completed" ||
                        booking.bookingDetails.status === "Cancelled" ||
                        booking.bookingDetails.status === "Refunded" ||
                        booking.bookingDetails.status === "Voided" ? (
                          <span className="text-green-600 font-medium">
                            {booking.bookingDetails.status}
                          </span>
                        ) : (
                          <>
                            <Countdown
                              key={targetTime} // ✅ Forces UI refresh if time changes
                              date={targetTime} // ✅ Uses the pure timestamp calculated above
                              renderer={timerRenderer}
                              onComplete={() => {
                                console.log(
                                  `Timer finished for ${booking.controlNumber}`,
                                );
                              }}
                            />

                            <button
                              onClick={() => {
                                setSelectedBookingId(booking.bookingDetails.id);

                                // ✅ Safely format the targetTime for the modal input
                                const currentTimer = new Date(targetTime);
                                const offset =
                                  currentTimer.getTimezoneOffset() * 60000;
                                const localISOTime = new Date(
                                  currentTimer.getTime() - offset,
                                )
                                  .toISOString()
                                  .slice(0, 16);

                                setNewTimerDate(localISOTime);
                                setIsTimerModalOpen(true);
                              }}
                              className="ml-3 text-indigo-500 hover:text-indigo-700 flex items-center gap-1 text-sm bg-indigo-50 px-2 py-1 rounded"
                              title="Edit Time & Notify Customer"
                            >
                              <Edit3 size={14} /> Edit
                            </button>
                          </>
                        )}
                      </span>

                      <span
                        className={`px-3 py-1 rounded-full text-sm ${
                          visualStatus === "Cancelled"
                            ? "bg-gray-300 text-black"
                            : visualStatus === "Completed"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {visualStatus}
                      </span>

                      <DeleteButton
                        onConfirm={() => handleDelete(booking.id)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500 mt-2">
                    <span>{booking.pairId}</span>
                    <span>{formatDate(booking.bookingDetails.createdAt)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <button
                    onClick={() => toggleExpand(booking.id.toString())}
                    className="flex items-center justify-between w-full text-left text-indigo-600 hover:text-indigo-800 transition-colors"
                  >
                    <span>
                      {expandedBookings.has(booking.id.toString())
                        ? "Hide Details"
                        : "Show Details"}
                    </span>
                    {expandedBookings.has(booking.id.toString()) ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                  {expandedBookings.has(booking.id.toString()) && (
                    <BookingDetails booking={booking} />
                  )}
                </CardContent>
              </Card>
            );
          })}

          <div className="flex justify-center items-center mt-6 space-x-4">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded ${
                currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-indigo-600 hover:bg-indigo-100"
              }`}
            >
              ← Previous
            </button>
            <p className="text-sm font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </p>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded ${
                currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-indigo-600 hover:bg-indigo-100"
              }`}
            >
              Next →
            </button>
          </div>
        </div>
      )}
      {/* ✅ ADDED: Timer Edit Modal */}
      {isTimerModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h3 className="text-xl font-bold mb-4">Edit Payment Timer</h3>
            <p className="text-sm text-gray-500 mb-4">
              Updating this will immediately send an Email and WhatsApp alert to
              the customer with the new deadline.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Expiration Date & Time
            </label>
            <input
              type="datetime-local"
              className="w-full border border-gray-300 p-2 rounded focus:ring-indigo-500 focus:border-indigo-500 mb-6"
              value={newTimerDate}
              onChange={(e) => setNewTimerDate(e.target.value)}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsTimerModalOpen(false)}
                disabled={isUpdatingTimer}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <PrimaryButton
                onClick={handleTimerUpdate}
                disabled={isUpdatingTimer}
                className="flex items-center gap-2 justify-center min-w-[150px]"
              >
                {/* ✅ Show spinner when loading, otherwise show text */}
                {isUpdatingTimer ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update & Notify"
                )}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
